// @greenlight-spec: 28-it-dashboard-ui.md#internal-health--internal-health-org-admin
import * as React from 'react';

import { cn } from '@/lib/utils';

import { useHistory } from './HistoryContext';
import { HISTORY_RANGE_ORDER, HISTORY_RANGES } from './historyModel';
import { formatWindowEdge, viewerTimeZone } from './historyTime';

/**
 * A History panel's own range, beside the charts it scopes. It says how much
 * of the range was observed and what markers there are, so a sparse first week
 * reads as young history rather than a quiet one.
 */
export function HistoryPanelHeader(): React.ReactElement {
  const { range, setRange, span, coverage, incidents, releasesFailed } = useHistory();
  const windowMs = HISTORY_RANGES[range].windowMs;
  const from = Date.parse(span.from);

  const observed = coverage?.coverage.observed_buckets;
  const expected = coverage?.coverage.expected_buckets;
  const availableFrom = coverage?.available_from ?? null;
  const parts = [
    observed === undefined || expected === undefined
      ? null
      : `Observed ${observed.toLocaleString()} of ${expected.toLocaleString()} intervals`,
    coverage === undefined
      ? null
      : availableFrom === null
        ? 'no history collected yet'
        : Date.parse(availableFrom) > from
          ? `history begins ${formatWindowEdge(Date.parse(availableFrom), 2 * 86_400_000)}`
          : null,
    incidents.isError
      ? 'incident markers could not be loaded'
      : incidents.hasMore
        ? `${incidents.items.length}+ incidents in this range, the newest shown`
        : incidents.items.length > 0
          ? `${incidents.items.length} incident${incidents.items.length === 1 ? '' : 's'} in this range`
          : null,
    releasesFailed ? 'release markers could not be loaded' : null,
  ].filter((part): part is string => part !== null);

  return (
    <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">
          {formatWindowEdge(from, windowMs)} to {formatWindowEdge(Date.parse(span.to), windowMs)} ·
          times in {viewerTimeZone()}
        </p>
        {parts.length > 0 && (
          <p className="text-xs text-muted-foreground" data-testid="history-coverage">
            {parts.join(' · ')}
          </p>
        )}
      </div>
      <div
        className="inline-flex items-center gap-1 rounded-gl bg-muted/60 p-0.5"
        role="group"
        aria-label="History range"
      >
        {HISTORY_RANGE_ORDER.map((key) => (
          <button
            key={key}
            type="button"
            aria-pressed={key === range}
            onClick={() => setRange(key)}
            className={cn(
              'h-7 rounded-gl px-2.5 text-[12px] transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
              key === range
                ? 'bg-primary font-semibold text-primary-foreground'
                : 'font-medium text-muted-foreground hover:text-foreground',
            )}
          >
            {key}
          </button>
        ))}
      </div>
    </div>
  );
}
